import { useMemo } from 'react';
import { useProject } from '@context/Project/ProjectProvider';
import { IDomain } from '@store/services/projects/types';
import {
  SCOPE_FILTER_NO_OPERATIONALISED,
  SCOPE_FILTER_UNASSIGNED,
  findWideDomain,
} from '@utils/domains';
import { transformDomainsToObject } from '@views/Project/helpers';

const useDomainsFilter = (domains: IDomain[]) => {
  const {
    toolbox: { domains: toolboxDomains },
    isDashboardMode,
  } = useProject();

  const ScopeName = useMemo(() => {
    if (!toolboxDomains.length || toolboxDomains.length === domains.length + 1)
      return '360° view';

    const domainsObject = transformDomainsToObject(domains, toolboxDomains);

    if (toolboxDomains.length === 1) {
      if (toolboxDomains[0] === SCOPE_FILTER_UNASSIGNED) {
        return 'Unassigned';
      }

      if (toolboxDomains[0] === SCOPE_FILTER_NO_OPERATIONALISED) {
        return 'No Operationalised scope';
      }

      return domainsObject[toolboxDomains[0]]?.name || 'Domain';
    }

    const wideDomain = findWideDomain(domains);
    const inheritedWide =
      !!wideDomain?.id && toolboxDomains.includes(wideDomain.id);

    const DomainId = toolboxDomains.find(
      (id) => id !== wideDomain?.id && domainsObject[id]?.level === 1,
    );

    if (toolboxDomains.length === 3) {
      const SubDomainId = toolboxDomains.find(
        (id) =>
          id !== wideDomain?.id &&
          domainsObject[id]?.parentId === DomainId &&
          domainsObject[id]?.level === 2,
      );

      if (inheritedWide && DomainId && SubDomainId) {
        return domainsObject[SubDomainId]?.name;
      }
    }

    if (toolboxDomains.length === 2) {
      if (inheritedWide && DomainId) {
        return domainsObject[DomainId]?.name;
      }
    }

    return 'Multiple Domains';
  }, [toolboxDomains, domains]);

  if (isDashboardMode) {
    return { ScopeName: '360° view', ScopeCount: domains.length + 1 };
  }

  return {
    ScopeName,
    ScopeCount: toolboxDomains.length || domains.length,
  };
};

export default useDomainsFilter;
